// src/components/ProtectedRoute.js
import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import LoadingAnimation from "../LoadingAnimation/LoadingAnimation";
import { supabase } from "../../supabaseClient"; // Import Supabase client

const ProtectedRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null); // null means loading state
  const [needsOnboarding, setNeedsOnboarding] = useState(null); // null while checking
  const location = useLocation();

  useEffect(() => {
    let mounted = true;
    const clearClientFlags = () => {
      try {
        localStorage.removeItem('payment_complete');
        localStorage.removeItem('payment_session_id');
        localStorage.removeItem('selected_plan_id');
        localStorage.removeItem('selected_plan_name');
        localStorage.removeItem('onboarding_profile');
      } catch (_) {}
    };

    const checkAuthAndProfile = async (sessionOverride = null) => {
      const { data: { session } } = sessionOverride
        ? { data: { session: sessionOverride } }
        : await supabase.auth.getSession();
      const authed = !!session;
      if (!mounted) return;
      setIsAuthenticated(authed);

      if (!authed) {
        setNeedsOnboarding(false);
        clearClientFlags();
        return;
      }

      const userId = session.user.id;

      // Default to needs onboarding; unset if we find a profile
      let hasProfile = false;
      let hasPayment = false;

      // 1) Try Supabase profiles table if it exists
      try {
        const { data, error, status } = await supabase
          .from('assessments')
          .select('assessment, completed, user_id')
          .eq('user_id', userId)
          .maybeSingle();

        if (!error && data) {
          // Consider profile present if completed or assessment exists
          hasProfile = !!data?.completed || !!data?.assessment;
        } else if (status === 406 || status === 200) {
          // 406 = no rows when using single; treat as no profile
        }
      } catch (_) {
        // Table may not exist; ignore
      }

      // 2) Fallback to localStorage profile saved by onboarding
      if (!hasProfile) {
        try {
          const local = localStorage.getItem('onboarding_profile');
          if (local) hasProfile = true;
        } catch (_) {}
      }

      // 3) Check payment status (Stripe) if the users table is available
      try {
        const { data } = await supabase
          .from('users')
          .select('payment_status')
          .eq('id', userId)
          .maybeSingle();
        hasPayment = data?.payment_status === 'paid' || data?.payment_status === 'trialing';
      } catch (_) {}

      setNeedsOnboarding(!hasProfile);
      try {
        localStorage.setItem('payment_complete', hasPayment ? 'true' : 'false');
      } catch (_) {}
    };

    checkAuthAndProfile();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      checkAuthAndProfile(session);
    });

    return () => {
      mounted = false;
      subscription?.unsubscribe();
    };
  }, [location.pathname]);

  // Still checking
  if (isAuthenticated === null || needsOnboarding === null) {
    return <LoadingAnimation fullScreen />;
  }

  // If not authenticated, redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  // Redirect to onboarding when needed, except when already on onboarding
  const path = typeof window !== 'undefined' ? window.location.pathname : '';
  const preOnboardingPaths = ['/select-plan', '/payment/success', '/payment/cancel'];
  let paymentComplete = false;
  try {
    paymentComplete = localStorage.getItem('payment_complete') === 'true';
  } catch (_) {}

  if (needsOnboarding) {
    if (!paymentComplete && !preOnboardingPaths.includes(path)) {
      return <Navigate to="/select-plan" />;
    }
    if (paymentComplete && path !== '/onboarding' && !preOnboardingPaths.includes(path)) {
      return <Navigate to="/onboarding" />;
    }
  }

  // If authenticated (and onboarding state satisfied), render the children
  return children;
};

export default ProtectedRoute;
